import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useFetch, { unsafeAPI } from '../../hooks/api';
import Firebase from '../../firebase_setup';
import { useBCVExchangeRate } from '../../hooks/firebase';
import { Dialog } from '../../components/Dialog';
import { DateRangePicker } from '../../shadcn/DateRangePicker';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import { Button } from '../../shadcn/Button';
import Table from '../../components/Table';
import {
	ArrowRightLeft,
	Banknote,
	BookUser,
	Building2,
	ChevronDown,
	Download,
	FileText,
	Landmark,
	Loader2,
	Mail,
	RefreshCw,
	Search,
	Send,
	Settings,
	ShieldCheck,
	Wallet,
	X,
} from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { validate_email } from '../../utils/validate';
import { API_BASE } from '../../utils/constants';
import SettlementControlPanel from './components/SettlementControlPanel';
import PayoutDetailDialog from './components/PayoutDetailDialog';
import ApprovePayoutsDialog from './components/ApprovePayoutsDialog';
import ChangeStatusDialog from './components/ChangeStatusDialog';
import Combobox from '../../components/Combobox';
import { TabBar } from '../../components/TabBar';
import TOTPAuth from '../../components/TOTP';

interface Proveedor {
	manual_payment_limit_usd?: number | null;
	id: string;
	name: string;
	identification_number: string;
	account_number: string;
	category: string;
	secondary_category?: string | null;
	is_active?: boolean;
	islr_retention_percent?: number | null;
	iva_retention_percent?: number | null;
}

const SUPPORT_FILE_MAX_BYTES = 10 * 1024 * 1024;
const SUPPORT_FILE_ALLOWED_TYPES = new Set([
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'text/plain',
]);
const MANUAL_PAYMENTS_PDF_EXCLUDED_COLUMNS = new Set([
	'numero de documento',
	'descripcion',
	'comentarios internos',
	'adjunto de soporte',
	'adjunto soporte',
	'operacion en divisas',
	'operacion en divisa',
	'tasa de cambio',
	'id transaccion',
	'id de transaccion',
	'error',
	'solicitado por',
	'pais',
	'ultima actualizacion',
	'id pago',
	'id de pago',
]);
const normalizeColumnName = (column: string) =>
	column
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();
const validateSupportFile = (file: File): string | null => {
	if (!SUPPORT_FILE_ALLOWED_TYPES.has(file.type)) return 'El adjunto debe ser Word, PDF o TXT.';
	if (file.size > SUPPORT_FILE_MAX_BYTES) return 'El adjunto no puede superar 10 MB.';
	return null;
};

export default function PagosAliados() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const country = internalCountry?.get() || '';
	const bcvRate = useBCVExchangeRate(country);
	const [dialogMessage, setDialogMessage] = useState('');
	const [activeTab, setActiveTab] = useState('aliados');

	// Store emails for combobox search
	const { data: storeEmails } = useFetch('https://api.krece.app/storeprofile/masteruser/', 'GET', true, undefined, undefined, true);

	// Account balances
	const [balances, setBalances] = useState<{ account_number: string; currency: string; balance: number }[]>([]);
	const [balancesLoading, setBalancesLoading] = useState(false);
	const [balancesError, setBalancesError] = useState('');

	// Payment Files and Reminders (All countries)
	const [paymentFilesLoading, setPaymentFilesLoading] = useState(false);
	const [remindersLoading, setRemindersLoading] = useState(false);
	const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
	const [excludeStores, setExcludeStores] = useState('');
	const [includeStores, setIncludeStores] = useState('');

	// Pending summary
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [summary, setSummary] = useState<{ total_usd: number; total_bs: number; bcv_rate: number; count: number } | null>(null);

	// Payouts table
	const [statusFilter, setStatusFilter] = useState<string | null>(null);
	const [dateFilter, setDateFilter] = useState<{ from?: string; to?: string }>({});
	const [tableKey, setTableKey] = useState(0);
	const [selectedPayout, setSelectedPayout] = useState<{ [key: string]: string | number } | null>(null);
	const [selectedPayouts, setSelectedPayouts] = useState<{ [key: string]: string | number }[]>([]);
	const [showApproveDialog, setShowApproveDialog] = useState(false);
	const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);

	// Select all across pages
	const [allPageSelected, setAllPageSelected] = useState(false);
	const [allFilteredSelected, setAllFilteredSelected] = useState(false);
	const [excludedEmails, setExcludedEmails] = useState<string[]>([]);
	const [excludedSummary, setExcludedSummary] = useState<{ count: number; total_usd: number; total_bs: number }>({
		count: 0,
		total_usd: 0,
		total_bs: 0,
	});

	// Manual Bank Payments table
	const [selectedManualPayments, setSelectedManualPayments] = useState<{ [key: string]: string | number }[]>([]);
	const [manualExportLoading, setManualExportLoading] = useState(false);

	// Manual Bank Payment
	const [manualBank, setManualBank] = useState<'bnc' | 'r4'>('bnc');
	const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
	const [manualAmount, setManualAmount] = useState('');
	const [manualConcept, setManualConcept] = useState('');
	const [manualDocumentNumber, setManualDocumentNumber] = useState('');
	const [manualDescription, setManualDescription] = useState('');
	const [manualInternalComments, setManualInternalComments] = useState('');
	const [manualSupportFile, setManualSupportFile] = useState<File | null>(null);
	const [manualSupportUrl, setManualSupportUrl] = useState('');
	const [manualSupportUploading, setManualSupportUploading] = useState(false);
	const [manualPaymentCategory, setManualPaymentCategory] = useState('');
	const [isForeignExchange, setIsForeignExchange] = useState(false);
	const [manualExchangeRate, setManualExchangeRate] = useState('');
	const [manualLoading, setManualLoading] = useState(false);
	const [showManualPaymentTOTP, setShowManualPaymentTOTP] = useState(false);

	// Proveedores search
	const [defaultManualLimit, setDefaultManualLimit] = useState<number | null>(null);
	const [proveedores, setProveedores] = useState<Proveedor[]>([]);
	const [proveedoresLoading, setProveedoresLoading] = useState(false);
	const [proveedoresFetched, setProveedoresFetched] = useState(false);
	const [proveedorSearch, setProveedorSearch] = useState('');
	const [proveedorCategoryFilter, setProveedorCategoryFilter] = useState('all');
	const [showProveedorResults, setShowProveedorResults] = useState(false);
	const proveedorSearchRef = useRef<HTMLDivElement>(null);

	const fetchProveedores = async () => {
		if (proveedoresFetched) return;
		setProveedoresLoading(true);
		try {
			const res = await unsafeAPI('GET', `${API_BASE}/proveedores/`, null, userData, internalCountry?.get());
			if (res?.success && res.proveedores) {
				setProveedores(res.proveedores);
				setDefaultManualLimit(res.manual_payment_default_limit_usd ?? null);
			}
			setProveedoresFetched(true);
		} catch {
			// silent fail
		} finally {
			setProveedoresLoading(false);
		}
	};

	const fetchBalances = async () => {
		setBalancesLoading(true);
		setBalancesError('');
		try {
			const res = await unsafeAPI('GET', `${API_BASE}/bnc_account_balances/`, null, userData, internalCountry?.get());
			if (res?.success && res.accounts) {
				setBalances(res.accounts);
			} else {
				setBalancesError(res?.detail || 'Error al obtener balances');
			}
		} catch {
			setBalancesError('Error al obtener balances');
		} finally {
			setBalancesLoading(false);
		}
	};

	useEffect(() => {
		fetchPendingSummary();
		fetchBalances();
	}, []);

	useEffect(() => {
		if (activeTab === 'manuales' && !proveedoresFetched) {
			fetchProveedores();
		}
	}, [activeTab]);

	// Close dropdown on outside click
	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (proveedorSearchRef.current && !proveedorSearchRef.current.contains(e.target as Node)) {
				setShowProveedorResults(false);
			}
		};
		document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, []);

	const handleExportCSV = async () => {
		if (!selectedPayouts.length) return;
		setExportLoading(true);
		try {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/export_transactions/`,
				{ payout_ids: selectedPayouts.map((p) => String(p['ID Payout'] || '')) },
				userData,
				internalCountry?.get()
			);
			if (response?.success && response.download_link) {
				const a = document.createElement('a');
				a.href = response.download_link;
				a.target = '_blank';
				a.click();
			} else {
				setDialogMessage(response?.detail || 'Error al exportar las transacciones.');
			}
		} catch {
			setDialogMessage('Error al exportar las transacciones.');
		} finally {
			setExportLoading(false);
		}
	};

	const handleExportManualPaymentsCSV = () => {
		if (!selectedManualPayments.length) return;
		setManualExportLoading(true);
		try {
			const columns = Object.keys(selectedManualPayments[0]);
			const csvHeader = columns.map((c) => `"${c}"`).join(',');
			const csvRows = selectedManualPayments.map((row) =>
				columns.map((c) => `"${String(row[c] ?? '').replace(/"/g, '""')}"`).join(',')
			);
			const csv = [csvHeader, ...csvRows].join('\n');
			const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `pagos_manuales_${new Date().toISOString().slice(0, 10)}.csv`;
			a.click();
			URL.revokeObjectURL(url);
		} catch {
			setDialogMessage('Error al exportar los pagos.');
		} finally {
			setManualExportLoading(false);
		}
	};

	const handlePrintManualPayments = () => {
		if (!selectedManualPayments.length) return;
		const columns = Object.keys(selectedManualPayments[0]).filter(
			(column) => !MANUAL_PAYMENTS_PDF_EXCLUDED_COLUMNS.has(normalizeColumnName(column))
		);
		const escapeHtml = (value: unknown) =>
			String(value ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
		const headerHtml = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
		const rowsHtml = selectedManualPayments
			.map((row) => `<tr>${columns.map((c) => `<td>${escapeHtml(row[c])}</td>`).join('')}</tr>`)
			.join('');
		const printWindow = window.open('', '_blank');
		if (!printWindow) {
			setDialogMessage('Habilite las ventanas emergentes para descargar el PDF.');
			return;
		}
		printWindow.document.write(
			'<!doctype html><html lang="es"><head><meta charset="utf-8" /><title>Pagos Manuales</title>' +
				'<style>body{font-family:system-ui,-apple-system,sans-serif;padding:24px;color:#111}' +
				'h1{font-size:18px;margin:0 0 16px}table{width:100%;border-collapse:collapse;font-size:12px}' +
				'th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#f3f4f6}</style></head>' +
				`<body><h1>Pagos Manuales (${selectedManualPayments.length})</h1>` +
				`<table><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`
		);
		printWindow.document.close();
		printWindow.focus();
		printWindow.print();
	};

	const fetchPendingSummary = async (startDate?: string, endDate?: string, status?: string | null) => {
		setSummaryLoading(true);
		try {
			const body: Record<string, string> = {};
			if (startDate && endDate) {
				body.start_date = startDate;
				body.end_date = endDate;
			}
			if (status && status !== 'all') {
				body.status = status;
			}
			const res = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/summary/`,
				body,
				userData,
				internalCountry?.get()
			);
			if (res?.success) {
				setSummary({
					total_usd: res.total_usd ?? 0,
					total_bs: res.total_bs ?? 0,
					bcv_rate: res.bcv_rate ?? 0,
					count: res.count ?? 0,
				});
			}
		} catch {
			// silent fail
		} finally {
			setSummaryLoading(false);
		}
	};

	const fetchExcludedEmailSummary = async (emails: string[]) => {
		if (emails.length === 0) {
			setExcludedSummary({ count: 0, total_usd: 0, total_bs: 0 });
			return;
		}
		try {
			let totalCount = 0;
			let totalUsd = 0;
			let totalBs = 0;

			for (const email of emails) {
				let lastObjectId: string | null = null;
				while (true) {
					const payload: any = {
						master_store_email: null,
						last_object_id: lastObjectId,
						batch_size: 500,
						search_by: { column_name: 'Email del Aliado', value: email },
						filter_by: [],
					};
					if (statusFilter && statusFilter !== 'all') {
						payload.filter_by.push({ column_name: 'Estado', value: statusFilter });
					}
					if (dateFilter.from && dateFilter.to) {
						payload.date_range = { from_date: dateFilter.from, to_date: dateFilter.to };
					}
					const res = await unsafeAPI(
						'POST',
						`${API_BASE}/tables/merchant_payouts/`,
						payload,
						userData,
						internalCountry?.get()
					);
					if (!res?.column_names || !res?.rows) break;

					const amountIdx = res.column_names.indexOf('Monto Neto');
					for (const row of res.rows) {
						totalCount++;
						const amount = Number(row[amountIdx]) || 0;
						totalUsd += amount;
						totalBs += amount * bcvRate;
					}

					if (!res.last_object_id || res.rows.length < 500) break;
					lastObjectId = res.last_object_id;
				}
			}
			setExcludedSummary({ count: totalCount, total_usd: totalUsd, total_bs: totalBs });
		} catch {
			// silent fail
		}
	};

	const handleExcludeEmail = (email: string) => {
		if (!email || excludedEmails.includes(email)) return;
		const updated = [...excludedEmails, email];
		setExcludedEmails(updated);
		fetchExcludedEmailSummary(updated);
	};

	const handleRemoveExcludedEmail = (email: string) => {
		const updated = excludedEmails.filter((e) => e !== email);
		setExcludedEmails(updated);
		fetchExcludedEmailSummary(updated);
	};

	const resetSelectAll = () => {
		setAllFilteredSelected(false);
		setAllPageSelected(false);
		setExcludedEmails([]);
		setExcludedSummary({ count: 0, total_usd: 0, total_bs: 0 });
	};

	const categoriesOf = (p: Proveedor) => [p.category, p.secondary_category].filter(Boolean) as string[];
	const proveedorCategories = [...new Set(proveedores.flatMap(categoriesOf))].sort();
	const selectedProveedorCategories = selectedProveedor ? [...new Set(categoriesOf(selectedProveedor))] : [];

	const filteredProveedores = proveedores.filter((p) => {
		if (p.is_active === false) return false;
		if (proveedorCategoryFilter !== 'all' && p.category !== proveedorCategoryFilter && p.secondary_category !== proveedorCategoryFilter) return false;
		if (proveedorSearch) {
			const term = proveedorSearch.toLowerCase();
			return (
				p.name.toLowerCase().includes(term) ||
				p.account_number.toLowerCase().includes(term) ||
				p.identification_number.toLowerCase().includes(term) ||
				p.category.toLowerCase().includes(term) ||
				(p.secondary_category || '').toLowerCase().includes(term)
			);
		}
		return true;
	});

	const formatAmountDisplay = (raw: string): string => {
		if (!raw) return '';
		const parts = raw.split('.');
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return parts.join('.');
	};

	// Reset the fields tied to the selected proveedor so a stale category / FX flag
	// from a previous proveedor can't be submitted against a different one.
	const clearProveedorPaymentFields = () => {
		setManualPaymentCategory('');
		setIsForeignExchange(false);
		setManualExchangeRate('');
	};

	const selectProveedorForManualPayment = (proveedor: Proveedor) => {
		setSelectedProveedor(proveedor);
		setManualPaymentCategory(proveedor.category);
		setIsForeignExchange(false);
		setManualExchangeRate('');
		setShowProveedorResults(false);
		setProveedorSearch('');
	};

	const uploadManualSupportFile = async (file: File): Promise<string> => {
		const error = validateSupportFile(file);
		if (error) throw new Error(error);

		const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
		const storage = Firebase.storage();
		const storageRef = storage.ref();
		const fileRef = storageRef.child(`manual_bank_payments/support/${Date.now()}_${safeName}`);
		await fileRef.put(file, { contentType: file.type });
		return fileRef.getDownloadURL();
	};

	const handleGeneratePaymentFiles = async () => {
		if (!dateRange?.from || !dateRange?.to) {
			setDialogMessage('Por favor seleccione las fechas de inicio y fin');
			return;
		}

		setPaymentFilesLoading(true);

		try {
			const excludeStoresList = excludeStores
				.split('\n')
				.map((store) => store.trim().toLowerCase())
				.filter((store) => store.length > 0);

			// Validate exclude stores emails
			for (const email of excludeStoresList) {
				if (!validate_email(email)) {
					setDialogMessage(`Email inválido en tiendas a excluir: ${email}`);
					return;
				}
			}

			const includeStoresList = includeStores
				.split('\n')
				.map((store) => store.trim().toLowerCase())
				.filter((store) => store.length > 0);

			// Validate include stores emails
			for (const email of includeStoresList) {
				if (!validate_email(email)) {
					setDialogMessage(`Email inválido en tiendas a incluir: ${email}`);
					return;
				}
			}

			const data = {
				start_date: dateRange.from.toISOString().split('T')[0],
				end_date: dateRange.to.toISOString().split('T')[0],
				exclude_stores: excludeStoresList,
				include_stores: includeStoresList,
			};

			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/generate_payment_files/`,
				data,
				userData,
				internalCountry?.get(),
				true,
				true
			);

			if (response?.success === true) {
				setDialogMessage('Archivos de pago generados correctamente.');
			} else {
				setDialogMessage(
					response?.status?.length ? 'Error: ' + response.status : 'Ocurrió un problema, intente más tarde.'
				);
			}
		} catch (err: any) {
			setDialogMessage('Error: ' + err.message);
		} finally {
			setPaymentFilesLoading(false);
		}
	};

	const handleSendReminders = async () => {
		if (!dateRange?.from || !dateRange?.to) {
			setDialogMessage('Por favor seleccione las fechas de inicio y fin');
			return;
		}

		setRemindersLoading(true);

		try {
			const excludeStoresList = excludeStores
				.split('\n')
				.map((store) => store.trim().toLowerCase())
				.filter((store) => store.length > 0);

			// Validate exclude stores emails
			for (const email of excludeStoresList) {
				if (!validate_email(email)) {
					setDialogMessage(`Email inválido en tiendas a excluir: ${email}`);
					return;
				}
			}

			const includeStoresList = includeStores
				.split('\n')
				.map((store) => store.trim().toLowerCase())
				.filter((store) => store.length > 0);

			// Validate include stores emails
			for (const email of includeStoresList) {
				if (!validate_email(email)) {
					setDialogMessage(`Email inválido en tiendas a incluir: ${email}`);
					return;
				}
			}

			const data = {
				start_date: dateRange.from.toISOString().split('T')[0],
				end_date: dateRange.to.toISOString().split('T')[0],
				exclude_stores: excludeStoresList,
				include_stores: includeStoresList,
			};

			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/send_payment_summary/`,
				data,
				userData,
				internalCountry?.get()
			);

			if (response?.success === true) {
				setDialogMessage('Resumen de pagos enviado correctamente.');
			} else {
				setDialogMessage(
					response?.status?.length ? 'Error: ' + response.status : 'Ocurrió un problema, intente más tarde.'
				);
			}
		} catch (err: any) {
			setDialogMessage('Error: ' + err.message);
		} finally {
			setRemindersLoading(false);
		}
	};

	const handleManualBankPayment = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!selectedProveedor || !manualAmount) {
			setDialogMessage('Por favor seleccione un proveedor e ingrese el monto');
			return;
		}

		const amountBs = parseFloat(manualAmount);
		if (!Number.isFinite(amountBs) || amountBs <= 0) {
			setDialogMessage('Ingrese un monto válido mayor a cero.');
			return;
		}
		const limit = selectedProveedor.manual_payment_limit_usd ?? defaultManualLimit;
		// The BCV hook returns 1 while unavailable; defer to backend validation in that case.
		if (Number.isFinite(bcvRate) && bcvRate > 1 && limit !== null) {
			const amountUsd = amountBs / bcvRate;
			if (amountUsd > limit) {
				setDialogMessage(
					`El monto de Bs ${formatAmountDisplay(manualAmount)} equivale a $${amountUsd.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD, lo cual excede el límite de $${limit.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD.`
				);
				return;
			}
		}

		if (selectedProveedorCategories.length > 1 && !manualPaymentCategory) {
			setDialogMessage('Seleccione la categoría bajo la cual se ejecutará este pago.');
			return;
		}
		const exchangeRate = parseFloat(manualExchangeRate);
		if (isForeignExchange && (!Number.isFinite(exchangeRate) || exchangeRate <= 0)) {
			setDialogMessage('La tasa de cambio es obligatoria para operaciones en divisas.');
			return;
		}

		// Upload the support attachment before requesting TOTP so an upload failure
		// doesn't waste the confirmation code. Skip if this file was already uploaded.
		if (manualSupportFile && !manualSupportUrl) {
			setManualSupportUploading(true);
			try {
				const url = await uploadManualSupportFile(manualSupportFile);
				setManualSupportUrl(url);
			} catch (err: any) {
				setDialogMessage(err.message || 'Error al subir el adjunto.');
				setManualSupportUploading(false);
				return;
			}
			setManualSupportUploading(false);
		}

		setShowManualPaymentTOTP(true);
	};

	const submitManualBankPayment = async (code: string) => {
		if (!selectedProveedor || !manualAmount) return;

		setManualLoading(true);

		try {
			const data: Record<string, any> = {
				proveedor_id: selectedProveedor.id,
				amount: parseFloat(manualAmount),
				bank: manualBank,
				confirmation_code: code,
			};

			if (manualConcept.trim()) {
				data.concept = manualConcept.trim();
			}
			if (manualPaymentCategory) data.payment_category = manualPaymentCategory;
			if (manualDocumentNumber.trim()) data.document_number = manualDocumentNumber.trim();
			if (manualDescription.trim()) data.description = manualDescription.trim();
			if (manualInternalComments.trim()) data.internal_comments = manualInternalComments.trim();
			if (manualSupportUrl) data.support_attachment_url = manualSupportUrl;
			data.is_foreign_exchange = isForeignExchange;
			if (isForeignExchange) data.exchange_rate = parseFloat(manualExchangeRate);

			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/send_bank_payment/`,
				data,
				userData,
				internalCountry?.get()
			);

			if (response?.success === true) {
				const count = response.batch_count || 1;
				const ids: string[] = response.payment_ids || [];
				setTableKey((k) => k + 1);
				setShowManualPaymentTOTP(false);
				setDialogMessage(
					`${count > 1 ? `${count} pagos enviados` : 'Pago enviado'} correctamente.${ids.length ? ` IDs: ${ids.join(', ')}` : ''} Procesando en segundo plano.`
				);
				setSelectedProveedor(null);
				setManualAmount('');
				setManualConcept('');
				setManualDocumentNumber('');
				setManualDescription('');
				setManualInternalComments('');
				setManualSupportFile(null);
				setManualSupportUrl('');
				setManualPaymentCategory('');
				setIsForeignExchange(false);
				setManualExchangeRate('');
			} else {
				setDialogMessage(response?.detail || 'Ocurrió un problema, intente más tarde.');
				setShowManualPaymentTOTP(false);
			}
		} catch (err: any) {
			setDialogMessage('Error: ' + err.message);
			setShowManualPaymentTOTP(false);
		} finally {
			setManualLoading(false);
		}
	};

	// Compute approval amounts for the dialog
	const approvalAmounts = (() => {
		if (allFilteredSelected) {
			return {
				totalUsd: (summary?.total_usd ?? 0) - excludedSummary.total_usd,
				totalBs: (summary?.total_bs ?? 0) - excludedSummary.total_bs,
			};
		}
		const vp =
			excludedEmails.length > 0
				? selectedPayouts.filter((p) => !excludedEmails.includes(String(p['Email del Aliado'] || '')))
				: selectedPayouts;
		const totalUsd = vp.reduce((sum, p) => sum + (Number(p['Monto Neto']) || 0), 0);
		const totalBs = vp.reduce((sum, p) => sum + (Number(p['Monto Neto']) || 0) * bcvRate, 0);
		return { totalUsd, totalBs };
	})();

	const tabs = [
		{ id: 'aliados', label: 'Pago a Aliados', icon: Banknote },
		{ id: 'manuales', label: 'Pagos Manuales', icon: Send },
		{ id: 'liquidacion', label: 'Control de Liquidación', icon: Settings },
	];

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-[1.25rem]">
			{dialogMessage && (
				<Dialog onClose={() => setDialogMessage('')} title="">
					<p>{dialogMessage}</p>
				</Dialog>
			)}

			{selectedPayout && (
				<PayoutDetailDialog
					payout={selectedPayout}
					liveBcvRate={bcvRate}
					onClose={() => setSelectedPayout(null)}
					onActionComplete={() => {
						setSelectedPayout(null);
						setTableKey((k) => k + 1);
					}}
				/>
			)}

			{showApproveDialog && (selectedPayouts.length > 0 || allFilteredSelected) && (
				<ApprovePayoutsDialog
					payoutIds={
						allFilteredSelected
							? []
							: (excludedEmails.length > 0
									? selectedPayouts.filter((p) => !excludedEmails.includes(String(p['Email del Aliado'] || '')))
									: selectedPayouts
								).map((p) => String(p['ID Payout'] || ''))
					}
					fetchAllConfig={
						allFilteredSelected
							? {
									statusFilter,
									dateFilter,
									excludeEmails: excludedEmails,
									totalCount: (summary?.count ?? 0) - excludedSummary.count,
								}
							: undefined
					}
					totalAmountUsd={approvalAmounts.totalUsd}
					totalAmountBs={approvalAmounts.totalBs}
					bcvRate={bcvRate}
					country={country}
					onClose={() => setShowApproveDialog(false)}
					onActionComplete={() => {
						setShowApproveDialog(false);
						setSelectedPayouts([]);
						resetSelectAll();
						setTableKey((k) => k + 1);
					}}
				/>
			)}

			{showChangeStatusDialog && selectedPayouts.length > 0 && (
				<ChangeStatusDialog
					payoutIds={selectedPayouts.map((p) => String(p['ID Payout'] || ''))}
					onClose={() => setShowChangeStatusDialog(false)}
					onActionComplete={() => {
						setShowChangeStatusDialog(false);
						setSelectedPayouts([]);
						setTableKey((k) => k + 1);
					}}
				/>
			)}

			{showManualPaymentTOTP && (
				<Dialog
					title="Verificación TOTP"
					onClose={() => setShowManualPaymentTOTP(false)}
					className="max-w-md"
					noContentWrapper
					cerrarButton={false}
				>
					<div className="no-scrollbar space-y-4 overflow-y-auto p-1 leading-normal text-neutral-200">
						<div className="flex items-center gap-3 rounded-lg bg-amber-500/10 px-3 py-2">
							<ShieldCheck className="size-5 shrink-0 text-amber-400" />
							<p className="text-sm text-amber-300">
								Confirma tu identidad para enviar el pago de{' '}
								<strong>Bs {formatAmountDisplay(manualAmount)}</strong>
								{bcvRate > 1 && (
									<span className="text-amber-400/70">
										{' '}
										(≈ $
										{(parseFloat(manualAmount || '0') / bcvRate).toLocaleString('en', {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}{' '}
										USD)
									</span>
								)}{' '}
								a <strong>{selectedProveedor?.name}</strong> vía {manualBank.toUpperCase()}.
							</p>
						</div>
						<TOTPAuth
							isSetUp={true}
							onSuccess={submitManualBankPayment}
							callVerifyEndpoint={false}
						/>
						{manualLoading && (
							<div className="flex items-center gap-2 text-sm text-neutral-400">
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-primary-500" />
								Enviando pago...
							</div>
						)}
						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="secondary" className="w-fit" onClick={() => setShowManualPaymentTOTP(false)} disabled={manualLoading}>
								Cancelar
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			<div className="space-y-8">
				{/* Header */}
				<div className="space-y-0 xl:text-center">
					<h2 className="text-2xl font-semibold text-neutral-100">Pagos a Aliados</h2>
					<p className="text-sm text-neutral-400">Gestión de pagos y resúmenes para aliados</p>
				</div>

				{/* Tab Bar */}
				<TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

				{/* Tab: Pago a Aliados */}
				{activeTab === 'aliados' && (
					<>
						{/* Balance en Cuentas */}
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="rounded-lg bg-emerald-500/10 p-2">
										<Wallet className="h-5 w-5 text-emerald-500" />
									</div>
									<h3 className="text-lg font-semibold text-neutral-100">Balance en Cuentas</h3>
								</div>
								<Button
									variant="secondary"
									className="!h-9 w-fit"
									onClick={fetchBalances}
									disabled={balancesLoading}
								>
									<RefreshCw className={`h-4 w-4 ${balancesLoading ? 'animate-spin' : ''}`} />
								</Button>
							</div>

							{balancesError && (
								<p className="text-sm text-red-400">{balancesError}</p>
							)}

							{/* BNC */}
							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<Landmark className="h-4 w-4 text-neutral-400" />
									<p className="text-sm font-semibold text-neutral-300">BNC</p>
								</div>
								{balancesLoading ? (
									<div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
										{[...Array(4)].map((_, i) => (
											<div key={i} className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
												<div className="h-3 w-20 animate-pulse rounded bg-neutral-800" />
												<div className="mt-3 h-7 w-28 animate-pulse rounded bg-neutral-800" />
											</div>
										))}
									</div>
								) : balances.length > 0 ? (
									<div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
										{balances.map((account) => (
											<div key={account.account_number} className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
												<p className="font-mono text-xs text-neutral-500">
													····{account.account_number.slice(-4)}
												</p>
												<p className="mt-1 text-xl font-semibold text-neutral-100">
													{account.currency === 'VES' ? 'Bs ' : '$ '}
													{account.balance.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
												</p>
												<p className="mt-0.5 text-xs text-neutral-500">{account.currency}</p>
											</div>
										))}
									</div>
								) : (
									!balancesError && <p className="text-sm text-neutral-500">No hay cuentas disponibles</p>
								)}
							</div>
						</div>
						{/* Pending Summary */}
						<div className="grid gap-4 md:grid-cols-3">
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
								<p className="text-xs text-neutral-400">Payouts pendientes</p>
								{summaryLoading ? (
									<div className="mt-2 h-7 w-24 animate-pulse rounded bg-neutral-800" />
								) : (
									<p className="mt-1 text-2xl font-semibold text-neutral-100">{summary?.count ?? '-'}</p>
								)}
							</div>
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
								<p className="text-xs text-neutral-400">Total USD</p>
								{summaryLoading ? (
									<div className="mt-2 h-7 w-32 animate-pulse rounded bg-neutral-800" />
								) : (
									<p className="mt-1 text-2xl font-semibold text-neutral-100">
										{summary ? `$${summary.total_usd.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
									</p>
								)}
							</div>
							{country === 'venezuela' && (
								<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
									<p className="text-xs text-neutral-400">
										Total Bs{' '}
										{summary && summary.bcv_rate > 1 && (
											<span className="text-neutral-500">
												(Tasa: {summary.bcv_rate.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
											</span>
										)}
									</p>
									{summaryLoading ? (
										<div className="mt-2 h-7 w-32 animate-pulse rounded bg-neutral-800" />
									) : (
										<p className="mt-1 text-2xl font-semibold text-neutral-100">
											{summary && summary.bcv_rate > 1
												? `Bs ${summary.total_bs.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
												: '-'}
										</p>
									)}
								</div>
							)}
						</div>

						{/* Payouts Table */}
						<div className="space-y-4">
							{(selectedPayouts.length > 0 || allFilteredSelected) && (() => {
								const visiblePayouts = excludedEmails.length > 0 && !allFilteredSelected
									? selectedPayouts.filter((p) => !excludedEmails.includes(String(p['Email del Aliado'] || '')))
									: selectedPayouts;
								const displayCount = allFilteredSelected
									? (summary?.count ?? 0) - excludedSummary.count
									: visiblePayouts.length;
								const displayTotalUsd = allFilteredSelected
									? (summary?.total_usd ?? 0) - excludedSummary.total_usd
									: visiblePayouts.reduce((sum, p) => sum + (Number(p['Monto Neto']) || 0), 0);
								const displayTotalBs = allFilteredSelected
									? (summary?.total_bs ?? 0) - excludedSummary.total_bs
									: visiblePayouts.reduce((sum, p) => sum + (Number(p['Monto Neto']) || 0) * bcvRate, 0);
								const canApprove = allFilteredSelected
									? statusFilter === 'pending' || statusFilter === 'failed'
									: visiblePayouts.length > 0 && visiblePayouts.every((p) => {
											const s = String(p['Estado'] || '').toLowerCase();
											return s === 'pending' || s === 'failed';
										});

								return (
								<div className="space-y-3">
									<div className="flex flex-col gap-3 rounded-lg border border-primary-500/30 bg-primary-500/5 p-3 md:flex-row md:items-center md:justify-between">
										<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-200">
											<span>
												<strong>{displayCount}</strong> payout
												{displayCount !== 1 ? 's' : ''} seleccionado
												{displayCount !== 1 ? 's' : ''}
												{excludedEmails.length > 0 && (
													<span className="text-neutral-400">
														{' '}({excludedEmails.length} excluido{excludedEmails.length !== 1 ? 's' : ''})
													</span>
												)}
											</span>
											<span className="text-neutral-400">|</span>
											<span>
												Total:{' '}
												<strong>
													{displayTotalUsd.toLocaleString('es', {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</strong>
												{allFilteredSelected
													? ' USD'
													: visiblePayouts[0]?.['Moneda'] ? ` ${visiblePayouts[0]['Moneda']}` : ''}
											</span>
											{country === 'venezuela' && bcvRate > 1 && (
												<>
													<span className="text-neutral-400">|</span>
													<span>
														Total Bs:{' '}
														<strong>
															{displayTotalBs.toLocaleString('es', {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})}
														</strong>
														{' '}
														<span className="font-normal text-neutral-400">
															(Tasa: {bcvRate.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
														</span>
													</span>
												</>
											)}
										</div>
										<div className="flex gap-2">
											{!allFilteredSelected &&
												visiblePayouts.every((p) => String(p['Estado'] || '').toLowerCase() === 'completed') &&
												visiblePayouts.length > 0 && (
													<Button
														variant="secondary"
														className="w-fit whitespace-nowrap"
														loading={exportLoading}
														onClick={handleExportCSV}
													>
														<Download className="h-4 w-4" />
														Exportar CSV ({visiblePayouts.length})
													</Button>
												)}
											{!allFilteredSelected && (
												<Button
													variant="secondary"
													className="w-fit whitespace-nowrap"
													onClick={() => setShowChangeStatusDialog(true)}
												>
													<ArrowRightLeft className="h-4 w-4" />
													Cambiar Estatus
												</Button>
											)}
											{canApprove && displayCount > 0 && (
												<Button
													variant="primary"
													className="w-fit whitespace-nowrap"
													onClick={() => setShowApproveDialog(true)}
												>
													<ShieldCheck className="h-4 w-4" />
													Aprobar ({displayCount})
												</Button>
											)}
											{allFilteredSelected && (
												<Button
													variant="secondary"
													className="w-fit whitespace-nowrap"
													onClick={resetSelectAll}
												>
													<X className="h-4 w-4" />
													Deseleccionar todo
												</Button>
											)}
										</div>
									</div>

									{/* Select all across pages banner — only for approvable statuses */}
									{allPageSelected &&
										!allFilteredSelected &&
										summary &&
										summary.count > selectedPayouts.length &&
										(statusFilter === 'pending' || statusFilter === 'failed') && (
										<div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-center text-sm text-neutral-200">
											{selectedPayouts.length} payouts en esta página seleccionados.{' '}
											<button
												className="font-semibold text-primary-400 underline underline-offset-2 hover:text-primary-300"
												onClick={() => setAllFilteredSelected(true)}
											>
												Seleccionar los {summary.count} payouts que coinciden con los filtros
											</button>
										</div>
									)}

									{/* Exclude emails section */}
									{(selectedPayouts.length > 0 || allFilteredSelected) && (
										<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
											<p className="mb-2 text-xs font-medium text-neutral-400">Excluir emails de la aprobación:</p>
											<div className="flex flex-wrap items-center gap-2">
												{excludedEmails.map((email) => (
													<span
														key={email}
														className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-xs text-red-300"
													>
														{email}
														<button
															className="ml-0.5 hover:text-red-100"
															onClick={() => handleRemoveExcludedEmail(email)}
														>
															<X className="size-3" />
														</button>
													</span>
												))}
												<div className="w-64">
													<Combobox
														key={`exclude-picker-${excludedEmails.length}`}
														options={
															storeEmails
																?.filter((e: string) => !excludedEmails.includes(e))
																.map((email: string) => ({ label: email, value: email })) || []
														}
														placeholder="Agregar email a excluir..."
														onSelectedChange={(value) => {
															if (value) handleExcludeEmail(String(value));
														}}
													/>
												</div>
											</div>
										</div>
									)}
								</div>
							);
							})()}

							<Table
								key={tableKey}
								userEmail={null}
								endpoint="https://api.krece.app/tables/merchant_payouts/"
								orderBy={{ columnName: 'Fecha de Creación', columnOrder: 'DESC' }}
								pagination={{ batchSizes: [50, 100, 200], default: 50 }}
								search_by={{
									column: 'Email del Aliado',
									placeholder: 'Buscar por email...',
									type: 'multi_combobox',
									options: storeEmails?.map((email: string) => ({ label: email, value: email })) || [],
								}}
								filters={[
									{
										column: 'Estado',
										options: [
											{ label: 'Todos', value: 'all' },
											{ label: 'Pendiente', value: 'pending' },
											{ label: 'Procesando', value: 'processing' },
											{ label: 'Completado', value: 'completed' },
											{ label: 'Fallido', value: 'failed' },
											{ label: 'Cancelado', value: 'cancelled' },
											{ label: 'Reintentando', value: 'retrying' },
										],
										defaultValue: 'all',
									},
								]}
								actionButton={{
									label: 'Ver Detalles',
									onClick: (row) => {
										setSelectedPayout(row);
									},
								}}
								selectable={{
									idColumn: 'ID Payout',
									onSelectionChange: (rows) => {
										setSelectedPayouts(rows);
										if (rows.length === 0 && !allFilteredSelected) {
											setExcludedEmails([]);
											setExcludedSummary({ count: 0, total_usd: 0, total_bs: 0 });
										}
									},
									onAllPageSelected: (allSelected) => {
										setAllPageSelected(allSelected);
										if (!allSelected) {
											setAllFilteredSelected(false);
										}
									},
								}}
								firstColumns={['Fecha de Venta', 'Fecha de Pago Efectivo', 'Email del Aliado', 'Titular']}
								truncateColumns={{ Titular: 600, 'Email del Aliado': 500 }}
								onDateRangeChange={(from, to) => {
									setDateFilter({ from: from ?? undefined, to: to ?? undefined });
									fetchPendingSummary(from ?? undefined, to ?? undefined, statusFilter);
								}}
								onFilterChange={(column, value) => {
									if (column === 'Estado') {
										const status = typeof value === 'string' ? value : null;
										setStatusFilter(status);
										fetchPendingSummary(dateFilter.from, dateFilter.to, status);
									}
								}}
							/>
						</div>

						{/* Payment Files and Reminders Section - All Countries */}
						<div className="space-y-6">
							<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
								<div className="rounded-lg bg-green-500/10 p-2">
									<FileText className="h-5 w-5 text-green-500" />
								</div>
								<h3 className="text-lg font-semibold text-neutral-100">
									Archivos de Pago y Resumen
								</h3>
							</div>

							<div className="space-y-6">
								{/* Date Range */}
								<div className="flex flex-col gap-4 md:flex-row md:gap-2 xl:mx-auto">
									<DateRangePicker
										className="w-full"
										floatingLabel="Período de fechas"
										onRangeChange={(range) => setDateRange(range)}
									/>
								</div>

								{/* Store Filters */}
								<div className="space-y-4 xl:mx-auto">
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<label className="text-sm font-medium text-neutral-200">
												Tiendas a Incluir
											</label>
											<textarea
												placeholder="Email de las tiendas a incluir (una por línea)"
												value={includeStores}
												onChange={(e) => setIncludeStores(e.target.value)}
												className="w-full resize-none rounded-lg border border-neutral-700 bg-neutral-800/50 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
												rows={6}
											/>
											<p className="!-mt-0.5 text-xs text-neutral-400">
												Ingrese los emails de las tiendas, uno por línea.
											</p>
										</div>

										<div className="space-y-2">
											<label className="text-sm font-medium text-neutral-200">
												Tiendas a Excluir
											</label>
											<textarea
												placeholder="Email de las tiendas a excluir (una por línea)"
												value={excludeStores}
												onChange={(e) => setExcludeStores(e.target.value)}
												className="w-full resize-none rounded-lg border border-neutral-700 bg-neutral-800/50 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
												rows={6}
											/>
											<p className="!-mt-0.5 text-xs text-neutral-400">
												Ingrese los emails de las tiendas a excluir, uno por línea.
											</p>
										</div>
									</div>
								</div>

								{/* Action Buttons */}
								<div className="flex flex-col gap-4 md:flex-row xl:mx-auto">
									<Button
										loading={paymentFilesLoading}
										variant="primary"
										onClick={handleGeneratePaymentFiles}
										className="flex-1"
										disabled={!dateRange?.from || !dateRange?.to}
									>
										<FileText className="h-4 w-4" />
										Generar Archivos de Pago
									</Button>

									<Button
										loading={remindersLoading}
										variant="primary"
										onClick={handleSendReminders}
										className="flex-1"
										disabled={!dateRange?.from || !dateRange?.to}
									>
										<Mail className="h-4 w-4" />
										Enviar Resumen de Pagos
									</Button>
								</div>
							</div>
						</div>
					</>
				)}

				{/* Tab: Pagos Manuales */}
				{activeTab === 'manuales' && (
					<div className="space-y-8">
						{/* Send Payment Section */}
						<div className="space-y-6">
							<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
								<div className="rounded-lg bg-primary-500/10 p-2">
									<Banknote className="h-5 w-5 text-primary-500" />
								</div>
								<h3 className="text-lg font-semibold text-neutral-100">Enviar Pago</h3>
							</div>

							{/* Proveedor Search */}
							<div className="space-y-3">
								<div className="flex items-center gap-2 text-sm font-medium text-neutral-300">
									<BookUser className="h-4 w-4" />
									Seleccionar proveedor
								</div>

								{selectedProveedor ? (
									<div className="flex items-center justify-between rounded-lg border border-primary-500/30 bg-primary-500/5 px-4 py-3">
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<span className="font-medium text-neutral-100">{selectedProveedor.name}</span>
												{selectedProveedorCategories.map((category) => (
													<span
														key={category}
														className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400"
													>
														{category}
													</span>
												))}
											</div>
											<div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-neutral-400">
												<span className="font-mono">{selectedProveedor.account_number}</span>
												<span>{selectedProveedor.identification_number}</span>
												{(selectedProveedor.manual_payment_limit_usd ?? defaultManualLimit) !== null && (
													<span>Límite por pago: USD {(selectedProveedor.manual_payment_limit_usd ?? defaultManualLimit)?.toLocaleString('en')}</span>
												)}
											</div>
										</div>
										<button
											onClick={() => {
												setSelectedProveedor(null);
												clearProveedorPaymentFields();
											}}
											className="shrink-0 rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300"
										>
											<X className="h-4 w-4" />
										</button>
									</div>
								) : (
									<div className="flex flex-col gap-3 md:flex-row">
										{/* Category Filter */}
										<div className="relative">
											<select
												value={proveedorCategoryFilter}
												onChange={(e) => setProveedorCategoryFilter(e.target.value)}
												className="h-11 appearance-none rounded-lg border border-neutral-700 bg-neutral-800/50 py-2 pl-3 pr-9 text-sm text-neutral-200 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
											>
												<option value="all">Todas las categorias</option>
												{proveedorCategories.map((cat) => (
													<option key={cat} value={cat}>
														{cat}
													</option>
												))}
											</select>
											<ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
										</div>

										{/* Search Input */}
										<div ref={proveedorSearchRef} className="relative flex-1">
											<div className="relative">
												<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
												<input
													type="text"
													placeholder="Buscar por nombre, cuenta o identificación..."
													value={proveedorSearch}
													onChange={(e) => {
														setProveedorSearch(e.target.value);
														setShowProveedorResults(true);
													}}
													onFocus={() => setShowProveedorResults(true)}
													className="h-11 w-full rounded-lg border border-neutral-700 bg-neutral-800/50 py-2 pl-10 pr-9 text-sm text-neutral-200 placeholder-neutral-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
												/>
												{proveedorSearch && (
													<button
														onClick={() => {
															setProveedorSearch('');
															setShowProveedorResults(false);
														}}
														className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
													>
														<X className="h-4 w-4" />
													</button>
												)}
											</div>

											{/* Results Dropdown */}
											{showProveedorResults && (
												<div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-800 shadow-xl">
													{proveedoresLoading ? (
														<div className="flex items-center justify-center gap-2 p-4 text-sm text-neutral-400">
															<Loader2 className="h-4 w-4 animate-spin" />
															Cargando proveedores...
														</div>
													) : filteredProveedores.length === 0 ? (
														<div className="p-4 text-center text-sm text-neutral-500">
															No se encontraron proveedores
														</div>
													) : (
														filteredProveedores.slice(0, 50).map((p) => (
															<button
																key={p.id}
																onClick={() => selectProveedorForManualPayment(p)}
																className="flex w-full items-center gap-3 border-b border-neutral-700/50 px-4 py-3 text-left transition-colors last:border-0 hover:bg-neutral-700/50"
															>
																<div className="min-w-0 flex-1">
																	<div className="flex items-center gap-2">
																		<span className="text-sm font-medium text-neutral-200">
																			{p.name}
																		</span>
																		<span className="truncate text-xs text-neutral-500">
																			{p.identification_number}
																		</span>
																	</div>
																	<span className="font-mono text-xs text-neutral-400">
																		{p.account_number}
																	</span>
																</div>
																<div className="flex shrink-0 flex-wrap justify-end gap-1">
																	{categoriesOf(p).map((category) => (
																		<span
																			key={category}
																			className="rounded bg-neutral-700 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400"
																		>
																			{category}
																		</span>
																	))}
																</div>
															</button>
														))
													)}
												</div>
											)}
										</div>
									</div>
								)}
							</div>

							{/* Bank Selector */}
							<div className="flex gap-2">
								<button
									onClick={() => setManualBank('bnc')}
									className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
										manualBank === 'bnc'
											? 'border-primary-500 bg-primary-500/10 text-primary-300'
											: 'border-neutral-700 bg-neutral-800/50 text-neutral-400 hover:border-neutral-600 hover:text-neutral-300'
									}`}
								>
									<Landmark className="h-4 w-4" />
									BNC
								</button>
								<button
									onClick={() => setManualBank('r4')}
									className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
										manualBank === 'r4'
											? 'border-primary-500 bg-primary-500/10 text-primary-300'
											: 'border-neutral-700 bg-neutral-800/50 text-neutral-400 hover:border-neutral-600 hover:text-neutral-300'
									}`}
								>
									<Building2 className="h-4 w-4" />
									R4
								</button>
							</div>

							<form className="space-y-4" onSubmit={handleManualBankPayment}>
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-1">
										<div className="relative flex h-12 items-center rounded-lg border border-neutral-800 bg-neutral-925 px-3 transition-all has-[:focus]:border-primary-500 has-[:focus]:outline-none has-[:focus]:ring-2 has-[:focus]:ring-primary-500 light:bg-neutral-950">
											<input
												type="text"
												inputMode="decimal"
												placeholder="Ingresar monto"
												value={formatAmountDisplay(manualAmount)}
												onChange={(e) => {
													const raw = e.target.value.replace(/[^0-9.]/g, '');
													const dotIdx = raw.indexOf('.');
													const clean =
														dotIdx === -1
															? raw
															: raw.slice(0, dotIdx + 1) +
																raw.slice(dotIdx + 1).replace(/\./g, '');
													const parts = clean.split('.');
													if (parts.length > 1 && parts[1].length > 2)
														parts[1] = parts[1].slice(0, 2);
													setManualAmount(parts.join('.'));
												}}
												className="w-full bg-transparent text-base outline-none placeholder:text-neutral-400 krece-input-floating-label"
											/>
											<label className="absolute -top-2 left-2 max-w-[90%] overflow-hidden overflow-ellipsis text-nowrap rounded bg-neutral-1000 px-1 text-xs font-medium text-primary-200/80 light:bg-primary-300 light:text-primary-900">
												Monto (Bs)
											</label>
											{manualAmount && (
												<button
													type="button"
													onClick={() => setManualAmount('')}
													className="mr-0.5 flex h-full shrink-0 items-center justify-center"
												>
													<X className="size-[1.125rem]" />
												</button>
											)}
										</div>
										{manualAmount && bcvRate > 1 && (
											<p className="text-xs text-neutral-400">
												≈ $
												{(parseFloat(manualAmount) / bcvRate).toLocaleString('en', {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}{' '}
												USD
											</p>
										)}
									</div>
									<Input
										placeholder="Pago de Krece"
										floatingLabel="Concepto (opcional)"
										value={manualConcept}
										onChange={(value) => setManualConcept(value)}
									/>
									{selectedProveedorCategories.length > 1 && (
										<Combobox
											options={selectedProveedorCategories.map((category) => ({ label: category, value: category }))}
											placeholder="Categoría del pago"
											floatingLabel="Categoría del pago"
											defaultValue={manualPaymentCategory || undefined}
											isDefaultValueNull={false}
											onSelectedChange={(value) => setManualPaymentCategory(value ? String(value) : '')}
										/>
									)}
									<Input
										placeholder="F-00012345"
										floatingLabel="Número de documento (opcional)"
										value={manualDocumentNumber}
										onChange={(value) => setManualDocumentNumber(value)}
									/>
									<label className="flex items-center gap-2 text-sm text-neutral-300">
										<input
											type="checkbox"
											checked={isForeignExchange}
											onChange={(e) => setIsForeignExchange(e.target.checked)}
											className="size-4 accent-primary-500"
										/>
										Operación en divisas
									</label>
									{isForeignExchange && (
										<Input
											placeholder="Tasa de cambio"
											floatingLabel="Tasa de cambio"
											value={manualExchangeRate}
											onChange={(value) => setManualExchangeRate(value.replace(/[^0-9.]/g, ''))}
										/>
									)}
								</div>
								<div className="grid gap-4 md:col-span-2 md:grid-cols-2">
									<Textarea
										floatingLabel="Descripción del pago (opcional)"
										placeholder="Descripción del pago"
										value={manualDescription}
										onChange={(value) => setManualDescription(value)}
										rows={3}
									/>
									<Textarea
										floatingLabel="Comentarios internos (opcional)"
										placeholder="No salen en comprobantes"
										value={manualInternalComments}
										onChange={(value) => setManualInternalComments(value)}
										rows={3}
									/>
								</div>
								<div className="space-y-2 md:col-span-2">
									<label className="text-sm font-medium text-neutral-300">Adjunto de soporte (opcional)</label>
									<input
										type="file"
										accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
										onChange={(e) => {
											const file = e.target.files?.[0] || null;
											setManualSupportUrl('');
											if (!file) {
												setManualSupportFile(null);
												return;
											}
											const error = validateSupportFile(file);
											if (error) {
												setDialogMessage(error);
												e.target.value = '';
												setManualSupportFile(null);
												return;
											}
											setManualSupportFile(file);
										}}
										className="block w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 py-2 text-sm text-neutral-300 file:mr-3 file:rounded-md file:border-0 file:bg-primary-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-50 hover:file:bg-primary-500 light:bg-neutral-950"
									/>
									<p className="text-xs text-neutral-500">Formatos permitidos: Word, PDF o TXT. Máximo 10 MB.</p>
								</div>

								<div className="flex justify-end">
									<Button
										loading={manualLoading || manualSupportUploading}
										variant="primary"
										className="!h-12 w-fit"
										type="submit"
										disabled={!selectedProveedor || !manualAmount || manualSupportUploading}
									>
										<Send className="h-4 w-4" />
										Enviar Pago ({manualBank.toUpperCase()})
									</Button>
								</div>
							</form>
						</div>

						{/* Manual Payments Table */}
						<div className="space-y-6">
							<div className="flex items-center justify-between border-b border-neutral-800 pb-3">
								<div className="flex items-center gap-3">
									<div className="rounded-lg bg-purple-500/10 p-2">
										<FileText className="h-5 w-5 text-purple-500" />
									</div>
									<h3 className="text-lg font-semibold text-neutral-100">Historial de Pagos Manuales</h3>
								</div>
								<Button
									variant="secondary"
									className="!h-9 w-fit"
									onClick={() => setTableKey((k) => k + 1)}
								>
									<RefreshCw className="h-4 w-4" />
									Recargar
								</Button>
							</div>

							{selectedManualPayments.length > 0 && (
								<div className="flex items-center justify-between rounded-lg border border-neutral-800 bg-neutral-900/50 px-4 py-3">
									<span className="text-sm text-neutral-300">
										{selectedManualPayments.length} pago{selectedManualPayments.length !== 1 ? 's' : ''} seleccionado{selectedManualPayments.length !== 1 ? 's' : ''}
									</span>
									<div className="flex gap-2">
										<Button
											variant="secondary"
											className="w-fit whitespace-nowrap"
											onClick={() => {
												const refs = selectedManualPayments.map((p) => String(p['Referencia'] || '')).filter(Boolean);
												if (!refs.length) {
													setDialogMessage('No hay referencias para copiar.');
													return;
												}
												navigator.clipboard
													.writeText(refs.join('\n'))
													.then(() => setDialogMessage(`${refs.length > 1 ? 'Referencias copiadas' : 'Referencia copiada'} al portapapeles.`))
													.catch(() => setDialogMessage('No se pudo copiar al portapapeles.'));
											}}
										>
											Copiar referencia
										</Button>
										<Button
											variant="secondary"
											className="w-fit whitespace-nowrap"
											onClick={handlePrintManualPayments}
										>
											Descargar PDF
										</Button>
										<Button
											variant="secondary"
											className="w-fit whitespace-nowrap"
											loading={manualExportLoading}
											onClick={handleExportManualPaymentsCSV}
										>
											<Download className="h-4 w-4" />
											Exportar CSV ({selectedManualPayments.length})
										</Button>
									</div>
								</div>
							)}

							<Table
								key={`manual-payments-${tableKey}`}
								userEmail={null}
								endpoint="https://api.krece.app/tables/manual_bank_payments/"
								orderBy={{ columnName: 'Fecha de Creación', columnOrder: 'DESC' }}
								pagination={{ batchSizes: [50, 100, 200], default: 50 }}
								search_by={{
									column: 'Número de Cuenta',
									placeholder: 'Buscar por cuenta...',
									type: 'combobox',
									options: proveedores.map((p) => ({
										label: `${p.name} — ${p.account_number}`,
										value: p.account_number,
									})),
								}}
								filters={[
									{
										column: 'Estado',
										options: [
											{ label: 'Todos', value: 'all' },
											{ label: 'Queued', value: 'queued' },
											{ label: 'Processing', value: 'processing' },
											{ label: 'Completed', value: 'completed' },
											{ label: 'Failed', value: 'failed' },
										],
										defaultValue: 'all',
									},
									{
										column: 'Banco',
										options: [
											{ label: 'Todos', value: 'all' },
											{ label: 'BNC', value: 'bnc' },
											{ label: 'R4', value: 'r4' },
										],
										defaultValue: 'all',
									},
								]}
								selectable={{
									idColumn: 'ID Pago',
									onSelectionChange: (rows) => setSelectedManualPayments(rows),
								}}
								firstColumns={['Fecha de Creación']}
								truncateColumns={{ Titular: 500, Concepto: 400, Descripción: 400, 'Comentarios Internos': 400 }}
							/>
						</div>
					</div>
				)}

				{/* Tab: Control de Liquidación */}
				{activeTab === 'liquidacion' && <SettlementControlPanel />}
			</div>
		</div>
	);
}
